import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import classNames from "classnames";

import Checkbox from "../../atoms/Checkbox/Checkbox";
import  {controllers as controllersRef } from "../../controllers";
import { assignApplianceToActuator, getActuatorNumberOfChannels, getApplianceCapacityPerChannel, getApplianceChannel, isApplianceConnectedToActuator, moveApplianceToActuatorChannel, removeApplianceFromActuator } from '../../Functions/ActuatorChannels';
import { addPublicURL } from "../../Functions/AddPublicURL";
import { formatEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { setSelectedActuator } from "../../redux/reducers/actuatorsRedurer";
import { setSelectedAppliance } from "../../redux/reducers/applianceReducer";
import { setActuators, setAppliances, setShowActuatorDetail, setShowDetectorDetail, setShowDeviceDetail, setShowThermostatDetail } from "../../redux/reducers/appReducer";
import { setSelectedDeviceId } from "../../redux/reducers/controllersReducer";
import { setSelectedDetector } from "../../redux/reducers/detectorsReducer";
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { DetectorStateI } from "../../redux/types/app";
import Button from "../Button/Button";

import styles from "./ActuatorDetail.module.scss";

const actuatorApplianceIcons: Record<string, string[]> = {
    'RFATV-2': ['11A'],
    'TELVA-3': ['11A']
};

const ActuatorDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const selectedActuatorSnapshot = useAppSelector((state) => state.actuators.actuator);
    const hasSelectedAppliance = useAppSelector((state) => state.app.showApplianceDetail);
    const allAppliances = useAppSelector((state) => state.app.appliances);
    const actuators = useAppSelector((state) => state.app.actuators);
    const selectedActuator = actuators.find((actuator) => actuator.id === selectedActuatorSnapshot.id) ?? selectedActuatorSnapshot;
    const compatibleApplianceIcons = actuatorApplianceIcons[selectedActuator.type];
    const appliances = allAppliances.filter((appliance) =>
        appliance.applianceType === selectedActuator.applianceType
        && (!compatibleApplianceIcons || compatibleApplianceIcons.includes(appliance.icon))
    );

    const connectedControllers = useAppSelector((state) => state.controllers.devices).filter((controller) => controller.iconsData.some((pos) => pos.connectedActuatorId === selectedActuator.id)) ?? null;
    const connectedDetectors: DetectorStateI[] = useAppSelector((state) => state.app.detectors).filter((detector) => (detector.connectedActuatorIds ?? []).includes(selectedActuator.id ?? -1));

    const syncActuator = (nextSelectedActuator: typeof selectedActuator) => {
        const actuatorId = selectedActuator.id;
        if (actuatorId === undefined) {
            return;
        }

        dispatch(setSelectedActuator(nextSelectedActuator));
        dispatch(
            setActuators(
                actuators.map((actuator) =>
                    actuator.id === actuatorId
                        ? nextSelectedActuator
                        : actuator
                )
            )
        );
    };

    const handleCheckboxClick = (id: number) => () => {
        const actuatorId = selectedActuator.id;

        if (actuatorId === undefined) {
            return;
        }

        const isConnected = isApplianceConnectedToActuator(selectedActuator, id);
        const nextSelectedActuator = isConnected
            ? removeApplianceFromActuator(selectedActuator, id)
            : assignApplianceToActuator(selectedActuator, id).actuator;

        if (!isConnected && !isApplianceConnectedToActuator(nextSelectedActuator, id)) {
            toast.error(t('actuatorDetail.errorChannelLimit', { count: getApplianceCapacityPerChannel(selectedActuator) }));
            return;
        }

        syncActuator(nextSelectedActuator);

        dispatch(
            setAppliances(
                allAppliances.map((appliance) =>
                    appliance.id === id
                        ? {
                            ...appliance,
                            actuator: isConnected ? undefined : actuatorId
                        }
                        : appliance
                )
            )
        );

        if(hasSelectedAppliance){
            const a = allAppliances.find((appliance) => appliance.id === id);
            if(a){
                dispatch(setSelectedAppliance({
                    id: a.id,
                    label: a.label,
                    icon: a.icon,
                    type: a.type,
                    applianceType: a.applianceType,
                    state: a.state ?? '',
                    actuator: isConnected ? 0 : actuatorId,
                    connectedTo: a.connectedTo ?? []
                }));
            }
        }
    };

    const handleChannelChange = (applianceId: number) => (event: React.ChangeEvent<HTMLSelectElement>) => {
        const targetChannel = Number(event.target.value);
        if (!targetChannel || !selectedActuator.id) {
            return;
        }

        const result = moveApplianceToActuatorChannel(selectedActuator, applianceId, targetChannel);
        if (!result.moved) {
            toast.error(t('actuatorDetail.errorChannelLimit', { count: getApplianceCapacityPerChannel(selectedActuator) }));
            return;
        }

        syncActuator(result.actuator);
    };

    const handleControllerClick = (controllerId: number, deviceType: string) =>{
        switch(deviceType){
            case 'controller':
                dispatch(setSelectedDeviceId(controllerId));
                dispatch(setShowDeviceDetail(true));
            break;
            case 'thermostat':
                dispatch(setShowThermostatDetail(true));
            break;
        }
        dispatch(setSelectedDeviceId(controllerId));
        dispatch(setShowDeviceDetail(true));
    }

    const handleDetectorClick = (detector: DetectorStateI) => {
        dispatch(setSelectedDetector(detector));
        dispatch(setShowDetectorDetail(true));
    }

    const getControllerPositionIndex = (controller: string, indexes: number[]): string => {
        const controllerRef = controllersRef.find((c) => c.name === controller);
        if (indexes.length === 0) {
            return '';
        }

        if(!controllerRef){
            return indexes.join(', ');
        }

        const positions = controllerRef.buttonCodes ?? [];
        return indexes
            .map((index) => (positions as string[])[index] ?? String(index))
            .join(', ');
    }

    const numberOfChannels = getActuatorNumberOfChannels(selectedActuator);

    return (
        <>
            <div className={styles.ActuatorDetail}>
                <div className={styles.ActuatorDetail__wrapper}>
                    <h2>{t('actuatorDetail.heading')}</h2>
                    <p>{t('actuatorDetail.description')}</p>
                    <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setShowActuatorDetail(false))} className={styles.ActuatorDetail__back} />
                
                    <div className={styles.ActuatorDetail__container}>
                        <div className={styles.ActuatorDetail__container__left}>
                            <div className={styles.ActuatorDetail__container__left__appliances}>
                                <div className={styles.ActuatorDetail__container__left__appliances__heading}>
                                    <p>{t('actuatorDetail.appliances.select')}</p>
                                    <span></span>
                                    <p>{t('actuatorDetail.appliances.id')}</p>
                                    <p>{t('actuatorDetail.appliances.name')}</p>
                                    <p>{t('actuatorDetail.appliances.channel')}</p>
                                    <p>{t('actuatorDetail.appliances.status')}</p>
                                </div>
                                <div className={styles.ActuatorDetail__container__left__appliances__list}>
                                    {appliances.map((appliance) => (
                                        ((appliance.actuator === selectedActuator.id) || (appliance.actuator === undefined)) && (
                                            <div key={appliance.id} className={styles.ActuatorDetail__container__left__appliances__item}>
                                                <div style={{ display: "flex", justifyContent: 'center' }}>
                                                    <Checkbox 
                                                        checked={isApplianceConnectedToActuator(selectedActuator, appliance.id)}
                                                        onChange={handleCheckboxClick(appliance.id)}
                                                        variant="outline"
                                                    >
                                                    </Checkbox>
                                                </div>
                                                <img 
                                                    className={styles.ActuatorDetail__container__left__appliances__item__icon}
                                                    src={getIconPath(appliance.icon, '', 'controllers/black')}
                                                    alt={appliance.icon}
                                                    draggable={false}
                                                />
                                                <p>{formatEntityId('appliance', appliance.id)}</p>
                                                <p>{appliance.label}</p>
                                                <div className={styles.ActuatorDetail__container__left__appliances__item__channel}>
                                                    {isApplianceConnectedToActuator(selectedActuator, appliance.id) ? (
                                                        <select
                                                            value={getApplianceChannel(selectedActuator, appliance.id) ?? 1}
                                                            onChange={handleChannelChange(appliance.id)}
                                                        >
                                                            {Array.from({ length: numberOfChannels }, (_, index) => index + 1).map((channel) => (
                                                                <option key={channel} value={channel}>{`CH ${channel}`}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </div>
                                                {appliance.state != undefined && (
                                                    <img className={styles.ActuatorDetail__container__left__appliances__item__state} src={getIconPath(appliance.state)} alt={appliance.state} />
                                                )}
                                            </div>
                                        )
                                    ))}
                                    {(() => { 
                                        const availableAppliances = appliances.filter(appliance => appliance.actuator === selectedActuator?.id || appliance.actuator === undefined);                                        
                                        if(availableAppliances.length === 0) {
                                            return <p>{t('actuatorDetail.appliances.noAvailable')}</p>;
                                        }
                                     })()}
                                </div>
                            </div>
                            <div className={styles.ActuatorDetail__container__left__save}>
                                <Button onClick={() => dispatch(setShowActuatorDetail(false))} size="lg" variant="fill">{t('apply')}</Button>
                            </div>
                        </div>
                        <span></span>
                        <div className={styles.ActuatorDetail__container__actuator}>
                            <div className={classNames(styles.ActuatorDetail__container__actuator__content, {
                                [styles.ActuatorDetail__container__actuator__content__dinrail]:  selectedActuator?.dinrail === true
                            })}>
                                <img 
                                    src={addPublicURL(`assets/actuators/${selectedActuator?.refImg ?? ''}`)}
                                />
                            </div>
                            <div className={styles.ActuatorDetail__container__actuator__info}>
                                <h3>{selectedActuator?.type} {selectedActuator?.name} | {formatEntityId('actuator', selectedActuator?.id ?? 0)}</h3>
                                <div style={{ marginTop: '1rem' }}>
                                    <h2>{t('actuatorDetail.numberOfChannels')}: {numberOfChannels}</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                    {selectedActuator?.applianceType !== 'hvac' && (
                        <div className={styles.ActuatorDetail__container__controller}>
                            <h3>{t('actuatorDetail.connectedController.heading')}</h3>
                            {(connectedControllers.length === 0) && (connectedDetectors.length === 0) ? (
                                <p>{t('actuatorDetail.connectedController.noConnected')}</p>
                            ) : (
                                <div className={styles.ActuatorDetail__container__controller__row}>
                                    {connectedControllers.map((connectedController) => (
                                        <div key={connectedController.id} className={styles.ActuatorDetail__container__controller__content}>
                                            <div className={styles.ActuatorDetail__container__controller__content__image}>
                                                {connectedController.configuredImageName ? (
                                                    <img 
                                                        src={`${process.env.REACT_APP_ICONS_URL}/source/userpanels/${connectedController.configuredImageName}`} 
                                                        alt={connectedController.name + connectedController.colorShortcut} 
                                                        onClick={() => handleControllerClick(connectedController.id, connectedController.type)}    
                                                    />
                                                ) : (
                                                    // <>
                                                    //     <p>{t('applianceDetail.no_design')}</p>
                                                    //     <span>{t('applianceDetail.no_design_description')}</span>
                                                    // </>
                                                    <img 
                                                        src={addPublicURL(`/assets/controllers/preview/${connectedController.previewImage + '-' + connectedController.color}.png`)}
                                                        alt={connectedController.name + connectedController.colorShortcut}
                                                        onClick={() => handleControllerClick(connectedController.id, connectedController.type)}
                                                    />
                                                )}
                                            </div>
                                            <p>{connectedController.name + connectedController.colorShortcut}</p>
                                            <p>ID: {formatEntityId('controller', connectedController.id)}</p>
                                            <p>{t('actuatorDetail.connectedController.connectedToPositions')} {getControllerPositionIndex(connectedController.name, connectedController.iconsData.filter(pos => pos.connectedActuatorId === selectedActuator?.id).map(pos => pos.position))}</p>
                                        </div>
                                    ))}
                                    {connectedDetectors.map((connectedDetector) => (
                                        <div key={connectedDetector.id} className={styles.ActuatorDetail__container__controller__content}>
                                            <div className={styles.ActuatorDetail__container__controller__content__image}
                                                onClick={() => handleDetectorClick(connectedDetector)}
                                            >
                                                <img src={addPublicURL(`assets/detectors/${connectedDetector.refImg}`)} alt={connectedDetector.name} />
                                            </div>
                                            <p>{connectedDetector.name}</p>
                                            <p>ID: {formatEntityId('detector', connectedDetector.id ?? 0)}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ActuatorDetail;
